import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  NeplaceniRacunTableDto,
  PocetnoStanjeRacunTableDto,
  RacuniClient,
  TipPristupa,
  UplataIsplataZaRacunDto,
  VrstaRacuna,
} from '@kodit/core/data-api';
import { AlertService, LocationService } from '@kodit/core/services';
import { DynamicDialogRef } from '@kodit/core/shared-ui';
import { BankaService } from 'libs/core/moduli/banka/src/lib/banka.service';
import {
  ActionType,
  OnTableInit,
  TableAction,
  TableConfig,
} from 'libs/core/shared-ui/src/lib/table/table-common';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'kodit-pocetno-stanje-racun-table',
  templateUrl: './pocetno-stanje-racun-table.component.html',
  styleUrls: ['./pocetno-stanje-racun-table.component.scss'],
})
export class PocetnoStanjeRacunTableComponent
  implements OnInit, OnDestroy, OnTableInit, OnChanges {
  /** Subs */
  private _getSub: Subscription = new Subscription();

  /** Configuration */
  tableConfig: TableConfig;
  tableActions: TableAction[];

  /** Props */
  racunTable: PocetnoStanjeRacunTableDto[] = [];
  jeDevizna: boolean;

  evidentiraniRacuni: number[] = [];

  /** I/O */
  @Input() dialogRef: DynamicDialogRef;
  @Input() uplataId: number = 0;
  @Input() ukupanIznosUplate: number = 0;
  @Input() vrstaRacuna: VrstaRacuna = VrstaRacuna.EMPTY;
  @Output() onEvidentirajUplatu = new EventEmitter<UplataIsplataZaRacunDto>();
  @Output() onPonistiEvidencijuUplate = new EventEmitter<number>();

  constructor(
    private _racuniClient: RacuniClient,
    private _locationService: LocationService,
    private _alertService: AlertService,
    private _bankaService: BankaService
  ) {}

  ngOnInit(): void {
    this._getSub.add(
      this._bankaService.getJeDevizniIzvod.subscribe(
        (res) => (this.jeDevizna = res)
      )
    );
    this._load();
    this.setTableConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.strankaId?.currentValue != changes.strankaId?.previousValue) {
      this.setTableConfig();
      this._load();
    }
  }

  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      tableHeader: '',
      columns: [
        {
          field: 'rutiranjeStranke',
          subField: 'naziv',
          header: 'Stranka',
          type: 'link',
          linkCallbackFunction: (itemIndex: number) =>
            this._goToStranka(itemIndex),
        },
        {
          field: 'preostaliIznos',
          header: 'Preostali iznos',
          type: 'currency',
        },
        {
          field: 'statusUplateDto',
          subField: 'statusStr',
          header: 'Status',
          styleClass: 'status-uplate-racuna',
          styleClassField: 'statusBadgeStr',
          tooltipField: 'opis',
          type: 'badge',
        },
      ],
      rowActions: [
        {
          type: ActionType.CUSTOM,
          tipPristupa: TipPristupa.IZVOD_CRUD,
          label: 'Evidentiraj uplatu',
          icon: 'fa-regular fa-check',
          hasAccessTooltip: 'Evidentirajte uplatu',
          noAccessTooltip: 'Nemate ovlašćenja za evidentiranje uplate',
          callback: (index: number) => this._onEvidencijaUplate(index),
          shouldDisplayByCondition: (rowData: NeplaceniRacunTableDto) => {
            return !this.evidentiraniRacuni.includes(rowData.id);
          },
        },
        {
          type: ActionType.CUSTOM,
          tipPristupa: TipPristupa.IZVOD_CRUD,
          label: 'Poništi evidenciju',
          icon: 'fa-regular fa-xmark',
          actionClass: 'p-button-danger',
          hasAccessTooltip: 'Poništite evidentiranu uplatu',
          callback: (index: number) =>
            this._onPonistavanjeEvidencijeUplate(index),
          shouldDisplayByCondition: (rowData: NeplaceniRacunTableDto) => {
            return this.evidentiraniRacuni.includes(rowData.id);
          },
        },
      ],
      updateActions$: new BehaviorSubject<number>(-1),
      rowsPerPage: 5,
    });
  }

  private _onEvidencijaUplate(rowIndex: number) {
    if (this.ukupanIznosUplate == 0) {
      this._alertService.addWarnMsg('Nema dovoljno sredstava');
      return;
    }
    let uplaceniIznos = 0;
    if (this.ukupanIznosUplate >= this.racunTable[rowIndex].preostaliIznos) {
      // imamo dovoljno sredstava pa cemo da skinemo ceo preoostali iznos
      uplaceniIznos = this.racunTable[rowIndex].preostaliIznos;
    } else {
      // nemamo dovoljno sredstava da isplatimo ceo racun, pa cemo to odraditi delimicno
      uplaceniIznos = this.ukupanIznosUplate;
    }
    this.evidentiraniRacuni.push(this.racunTable[rowIndex].id);
    this.onEvidentirajUplatu.emit(
      new UplataIsplataZaRacunDto({
        racunId: this.racunTable[rowIndex].id,
        iznos: uplaceniIznos,
      })
    );
    this.tableConfig.setUpdateActions = rowIndex;
  }

  private _onPonistavanjeEvidencijeUplate(rowIndex: number) {
    const idx = this.evidentiraniRacuni.findIndex(
      (x) => x === this.racunTable[rowIndex].id
    );
    this.evidentiraniRacuni.splice(idx, 1);
    this.onPonistiEvidencijuUplate.emit(this.racunTable[rowIndex].id);
    this.tableConfig.setUpdateActions = rowIndex;
  }

  private _goToStranka(itemIndex: number) {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this._locationService.routeToStrankaKartica(
      this.racunTable[itemIndex].rutiranjeStranke.strankaId,
      this.racunTable[itemIndex].rutiranjeStranke.tipStranke
    );
  }

  private _load() {
    this._getSub = this._racuniClient
      .getPocetnoStanjeRacunTable(this.vrstaRacuna, this.jeDevizna)
      .subscribe((res) => {
        this.racunTable = res.data.pocetnoStanjeRacunTableDtoList;
      });
  }

  ngOnDestroy(): void {
    this._getSub.unsubscribe();
  }
}
